import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { StatusBadge } from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Plus, BookOpen, Pencil, Trash2, X, Search, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";

const GRADE_SUBJECTS = {
  "8": ["Maths - Ganita Prakash I", "Maths - Ganita Prakash II"],
  "9": ["Maths - Ganita Manjari I", "Maths - Ganita Manjari II"],
};
const LOCKED_SUBJECTS = Object.values(GRADE_SUBJECTS).flat();

const blankForm = {
  name: "",
  subject: "Science",
  grade: "8",
  icon: "book",
  objectives: [""],
  outcomes: [""],
  estimated_time: "20 min",
  teacher_status: "Published",
  default_status: "Not Started",
};

export default function Modules({ role = "admin" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [editing, setEditing] = useState(null); // module object or "new"
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/admin/modules").then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [i18n.resolvedLanguage]);

  const openNew = () => { setForm(blankForm); setEditing("new"); };
  const openEdit = (m) => {
    setForm({
      name: m.name, subject: m.subject, grade: m.grade || "", icon: m.icon || "book",
      objectives: m.objectives?.length ? m.objectives : [""],
      outcomes: m.outcomes?.length ? m.outcomes : [""],
      estimated_time: m.estimated_time, teacher_status: m.teacher_status,
      default_status: m.default_status || "Not Started",
    });
    setEditing(m);
  };

  const close = () => { setEditing(null); setForm(blankForm); };

  const updList = (key, i, v) => setForm((f) => ({ ...f, [key]: f[key].map((x, k) => (k === i ? v : x)) }));
  const addListItem = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const removeListItem = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, k) => k !== i).length ? f[key].filter((_, k) => k !== i) : [""] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        objectives: form.objectives.map((s) => s.trim()).filter(Boolean),
        outcomes: form.outcomes.map((s) => s.trim()).filter(Boolean),
      };
      if (editing === "new") {
        await api.post("/admin/modules", payload);
        toast.success("Module created. 3 starter questions auto-generated.");
      } else {
        await api.put(`/admin/modules/${editing.id}`, payload);
        toast.success("Module updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/admin/modules/${confirmDel.id}`);
      toast.success(`Deleted module • ${data.deleted.questions} questions • ${data.deleted.progress} progress records removed`);
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = items.filter((m) => {
    const q = search.toLowerCase();
    const matchesSearch = m.name.toLowerCase().includes(q) || m.subject.toLowerCase().includes(q);
    const matchesGrade = gradeFilter === "all" || (m.grade || "") === gradeFilter;
    const matchesSubject = subjectFilter === "all" || m.subject === subjectFilter;
    return matchesSearch && matchesGrade && matchesSubject;
  });

  const submodulesPath = (id) => role === "teacher" ? `/teacher/manage/modules/${id}/submodules` : `/admin/modules/${id}/submodules`;

  return (
    <AppShell role={role}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Chapters</h1>
          <p className="mt-1 text-slate-500">Create, edit and remove chapters. Changes reflect instantly in teacher and student portals.</p>
        </div>
        <button onClick={openNew} data-testid="add-module-btn" className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">
          <Plus size={16} /> Add Chapter
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="search-modules-admin" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or subject…" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
          </div>
          <select data-testid="filter-grade" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
            <option value="all">All Grades</option>
            <option value="8">Grade 8</option>
            <option value="9">Grade 9</option>
            <option value="10">Grade 10</option>
            <option value="">Unassigned</option>
          </select>
          <select data-testid="filter-subject" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100">
            <option value="all">All Subjects</option>
            <option value="Science">Science</option>
            <option value="Maths">Maths</option>
            <option value="Maths - Ganita Prakash I">Maths - Ganita Prakash I</option>
            <option value="Maths - Ganita Prakash II">Maths - Ganita Prakash II</option>
            <option value="Maths - Ganita Manjari I">Maths - Ganita Manjari I</option>
            <option value="Maths - Ganita Manjari II">Maths - Ganita Manjari II</option>
          </select>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">Chapter Name</th>
                <th className="py-3 pr-4">Grade</th>
                <th className="py-3 pr-4">Subject</th>
                <th className="py-3 pr-4">Status (Teacher)</th>
                {role !== "teacher" && <th className="py-3 pr-4">Status (Student)</th>}
                <th className="py-3 pr-4">Questions</th>
                {role !== "teacher" && <th className="py-3 pr-4">Time</th>}
                <th className="py-3 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{m.display_name || m.name}</td>
                  <td className="py-4 pr-4">
                    {m.grade ? (
                      <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Grade {m.grade}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-4 pr-4">
                    <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${m.subject === "Science" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"}`}>{m.subject}</span>
                  </td>
                  <td className="py-4 pr-4"><StatusBadge value={m.teacher_status} /></td>
                  {role !== "teacher" && <td className="py-4 pr-4"><StatusBadge value={m.default_status || "Not Started"} /></td>}
                  <td className="py-4 pr-4 text-slate-700">{m.questions_count}</td>
                  {role !== "teacher" && <td className="py-4 pr-4 text-slate-600">{m.estimated_time}</td>}
                  <td className="py-4 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => navigate(submodulesPath(m.id))} data-testid={`submodules-${m.id}`} className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100">
                        <Layers size={12} /> Submodules
                      </button>
                      <button onClick={() => openEdit(m)} data-testid={`edit-module-${m.id}`} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                        <Pencil size={12} /> Edit
                      </button>
                      {(role !== "teacher" || m.created_by === user?.id) && (
                        <button onClick={() => setConfirmDel(m)} data-testid={`delete-module-${m.id}`} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100">
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="py-12 text-center text-sm text-slate-500">No modules match your search.</p>}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm">
          <form onSubmit={save} className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" data-testid="module-modal">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600"><BookOpen size={18} /></div>
                <h3 className="text-xl font-bold text-slate-900">{editing === "new" ? "Add Module" : "Edit Module"}</h3>
              </div>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Chapter Name" className="sm:col-span-2">
                <input required data-testid="module-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} placeholder="e.g. Gravitation" />
              </Field>
              <Field label="Subject">
                <select required data-testid="module-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inp}>
                  <option>Science</option>
                  <option>Maths</option>
                  {(GRADE_SUBJECTS[form.grade] || []).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Grade">
                <select required data-testid="module-grade" value={form.grade} onChange={(e) => {
                  const g = e.target.value;
                  setForm((f) => ({ ...f, grade: g, subject: (LOCKED_SUBJECTS.includes(f.subject) && !(GRADE_SUBJECTS[g] || []).includes(f.subject)) ? "Science" : f.subject }));
                }} className={inp}>
                  <option value="">Select grade…</option>
                  <option value="8">Grade 8</option>
                  <option value="9">Grade 9</option>
                  <option value="10">Grade 10</option>
                </select>
              </Field>
              <Field label="Estimated Time">
                <input required data-testid="module-time" value={form.estimated_time} onChange={(e) => setForm({ ...form, estimated_time: e.target.value })} className={inp} placeholder="20 min" />
              </Field>
              <Field label="Teacher-side Status">
                <select data-testid="module-teacher-status" value={form.teacher_status} onChange={(e) => setForm({ ...form, teacher_status: e.target.value })} className={inp}>
                  <option>Published</option>
                  <option>In Progress</option>
                  <option>Not Started</option>
                </select>
              </Field>
              <Field label="Default Student Status">
                <select data-testid="module-default-status" value={form.default_status} onChange={(e) => setForm({ ...form, default_status: e.target.value })} className={inp}>
                  <option>Not Started</option>
                  <option>Coming Soon</option>
                </select>
              </Field>
            </div>

            <ListField label="Objectives" values={form.objectives} onChange={(i, v) => updList("objectives", i, v)} onAdd={() => addListItem("objectives")} onRemove={(i) => removeListItem("objectives", i)} placeholder="e.g. Understand Newton's laws of motion" testPrefix="obj" />
            <ListField label="Learning Outcomes" values={form.outcomes} onChange={(i, v) => updList("outcomes", i, v)} onAdd={() => addListItem("outcomes")} onRemove={(i) => removeListItem("outcomes", i)} placeholder="e.g. Apply F = ma to real-world problems" testPrefix="out" />

            {editing === "new" && (
              <p className="mt-5 rounded-xl bg-violet-50 p-3 text-xs text-violet-700">
                3 starter quiz questions (Conceptual / Applied / Theoretical) will be auto-generated for this module. You can refine them later.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={close} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button data-testid="module-save" disabled={saving} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
                {saving ? "Saving…" : editing === "new" ? "Create Module" : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm" data-testid="delete-confirm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600"><AlertTriangle size={20} /></div>
              <h3 className="text-xl font-bold text-slate-900">Delete "{confirmDel.name}"?</h3>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              This permanently removes the module along with its <span className="font-semibold">{confirmDel.questions_count} quiz questions</span> and all related student progress entries. This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-module" className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-700">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const inp = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
    {children}
  </label>
);

const ListField = ({ label, values, onChange, onAdd, onRemove, placeholder, testPrefix }) => (
  <div className="mt-5">
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <button type="button" onClick={onAdd} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">+ Add item</button>
    </div>
    <div className="space-y-2">
      {values.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input
            data-testid={`${testPrefix}-input-${i}`}
            value={v}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder={placeholder}
            className={`${inp} flex-1`}
          />
          <button type="button" onClick={() => onRemove(i)} className="rounded-xl border border-slate-200 bg-white px-3 text-slate-500 hover:bg-rose-50 hover:text-rose-600">
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

